import {
  Box,
  type OverridableStringUnion,
  Skeleton,
  Stack,
  styled,
  Typography,
  type TypographyPropsVariantOverrides,
  type TypographyVariant,
} from "~/common/mui";
import { memo } from "react";
import { useTranslation } from "react-i18next";
import { AppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/AppliedFilters";
import { SearchFilters } from "~/features/search/components/Filters/SearchFilters";
import { selectVisibleFilters } from "~/features/search/redux/filterConfigSelectors";
import { useSelector } from "react-redux";
import { selectProductResultAmount } from "~/features/search/redux/searchSelectors";
import { isComingFromSeriesTreeClick } from "~/features/search/redux/seriesTreeSelectors";
import { Filter } from "~/features/search/models/filterConfigModels";
import { SearchDisplayMode } from "~/features/search/models/models";
import { SearchPageType } from "~/features/search/models/SearchPageType";
import { useSearchPageType } from "~/features/search/context/SearchPageTypeContext";
import type { RootState } from "~/app/store";
import { useSelectAppliedFilters } from "~/features/search/components/Filters/components/AppliedFilters/useSelectAppliedFilters";
import { FilterSectionSkeleton } from "./components/FilterSectionSkeleton";
import { selectSearchDisplayMode } from "~/domains/userPreferences/redux/selectors";
import { theme } from "~/app/theme";

interface Props {
  titleFontVariant?: OverridableStringUnion<TypographyVariant | "inherit", TypographyPropsVariantOverrides>;
  hideTechFilterButtons?: boolean;
  loading?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  methods?: any;
  onSubmit?: (data: Record<string, { min?: number | undefined; max?: number | undefined }>) => void;
}

export const ReduxFilterSection = memo(function ReduxFilterSection({
  titleFontVariant = "headlineSmall",
  hideTechFilterButtons,
  loading,
  methods,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const isTabularDisplay = useSelector(selectSearchDisplayMode) === SearchDisplayMode.TABULAR;
  const isProductFamilySelected = useSelector(selectIsProductFamilySelected);
  const isPlp = useSearchPageType() === SearchPageType.PLP;

  const hideNonCategoryFilters = isTabularDisplay && !isProductFamilySelected && !isPlp;

  let filterConfig = useSelector(selectVisibleFilters);

  const productAmount = useSelector(selectProductResultAmount);
  const isComingFromSeriesTree = useSelector(isComingFromSeriesTreeClick);

  const appliedFilters = useSelectAppliedFilters();

  if (!loading && isComingFromSeriesTree) {
    filterConfig = filterConfig.filter((item) => item.type !== Filter.TAXONOMY);
  }

  if (!loading && !productAmount && appliedFilters.length === 0 && filterConfig.length === 0) {
    return null;
  }

  const title = (
    <Box
      sx={{
        [theme.breakpoints.up("desktop-s")]: {
          height: "64px",
          position: "sticky",
          px: 1.5,
          top: 0,
          zIndex: 2,
          backgroundColor: theme.palette.surface.bright,
        },
      }}
    >
      <Typography
        variant={titleFontVariant}
        sx={{
          fontWeight: "bold",
          [theme.breakpoints.up("desktop-s")]: {
            pt: 2,
            pb: 2,
          },
        }}
      >
        {loading ? <Skeleton /> : t("page.search.filters.title")}
      </Typography>
    </Box>
  );

  return (
    <Stack>
      {title}
      <StyledFilterStack spacing={{ phone: 1, "desktop-s": 2 }}>
        {loading && <FilterSectionSkeleton amount={5} />}
        {!loading && (
          <>
            <AppliedFilters appliedFilters={appliedFilters} hideClearAll={hideTechFilterButtons} />
            <SearchFilters
              filterConfig={filterConfig}
              hideTechFilterButtons={hideTechFilterButtons}
              hideNonCategoryFilters={hideNonCategoryFilters}
              methods={methods}
              onSubmit={onSubmit}
            />
          </>
        )}
      </StyledFilterStack>
    </Stack>
  );
});

const selectIsProductFamilySelected = (state: RootState) => state.srp.facets.selectedProductGroups.length > 0;

const StyledFilterStack = styled(Stack)(({ theme }) => ({
  background: theme.palette.surface.bright,
  overflowY: "auto",
  maxHeight: "calc(100vh - 80px)",
  [theme.breakpoints.down("desktop-m")]: {
    overflowY: "visible",
    maxHeight: "auto",
  },
  "&::-webkit-scrollbar": {
    width: "8px",
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    backgroundColor: "transparent",
    borderRadius: "4px",
    transition: "background-color 0.2s ease-in-out",
  },
  scrollbarWidth: "thin",
  scrollbarColor: "transparent transparent",
  "&:hover": {
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.palette.text.secondary,
    },
    scrollbarColor: `${theme.palette.text.secondary} transparent`,
  },
  transition: "scrollbar-color 0.2s, scrollbar-width 0.2s",
}));
